import type { Step as RcStep, Robot } from '@sb/remote-control/types';

import { type ValidationRule, type ValidationContext } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class SetIORule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.SetIO', 'SetIO');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    type SetIOArgs = Extract<RcStep.Arguments, { argumentKind: 'SetIO' }>;
    const a = args as SetIOArgs;
    this.checkOutputsConfigured(a);
    const usedOutputs = this.getUsedOutputs(a, ctx);
    this.checkNoSafetyOutputs(usedOutputs);
  }

  private checkOutputsConfigured(
    args: Extract<RcStep.Arguments, { argumentKind: 'SetIO' }>,
  ): void {
    /**
     * Ensure at least one output is configured.
     * Throws ActionRequiredError if missing.
     *
     * @throws {ActionRequiredError}
     */
    const outputs = args.outputs as unknown[];

    if (!Array.isArray(outputs) || outputs.length === 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'At least one output must be selected',
        fieldId: 'outputs',
      });
    }
  }

  private getUsedOutputs(
    args: Extract<RcStep.Arguments, { argumentKind: 'SetIO' }>,
    ctx: ValidationContext,
  ): Robot.IOPort[] {
    /**
     * Compute outputs referenced by this step that exist in context.
     *
     * @returns {any[]} Matched outputs
     */
    const outputs = args.outputs as Array<{
      port: number;
      kind: Robot.IOPort['kind'];
    }>;

    const io = (ctx.ioOutputs ?? []) as Robot.IOPort[];

    return io.filter((output) =>
      outputs.some(
        (argOutput) =>
          argOutput.port === output.port && argOutput.kind === output.kind,
      ),
    );
  }

  private checkNoSafetyOutputs(usedOutputs: Robot.IOPort[]): void {
    /**
     * Ensure outputs assigned to Safety I/O are not settable.
     * Throws ActionRequiredError if any are referenced.
     *
     * @throws {ActionRequiredError}
     */
    const usedSafetyIOs = usedOutputs.filter(
      (output) => output.assignedTo?.kind === 'safetyIO',
    );

    if (usedSafetyIOs.length > 0) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Safety I/O outputs cannot be set',
        fieldId: 'outputs',
      });
    }
  }
}

export const SetIOValidation: ValidationRule =
  new SetIORule().toValidationRule();
